import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, 
  TrendingUp, 
  Download,
  Search,
  RefreshCw,
  ArrowUpDown,
  Calendar,
  Mail,
  Phone,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const concernLabels = {
  budget: "Budget",
  neighborhood: "Neighborhood",
  application: "Application",
  moving: "Moving"
};

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total_leads: 0, today_leads: 0, concern_breakdown: {} });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/stats`)
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/leads/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export downloaded successfully!");
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Failed to export leads");
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const filteredLeads = leads
    .filter(lead => 
      lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === "created_at") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  // Prepare chart data
  const chartData = Object.entries(stats.concern_breakdown).map(([key, value]) => ({
    name: concernLabels[key] || key || "Not specified",
    count: value
  }));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 sm:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 
              data-testid="admin-title"
              className="text-3xl font-bold text-[#1E3A5F]"
            >
              Lead Dashboard
            </h1>
            <p className="text-[#64748B] mt-1">
              Urban Nest Quest — Lead Management
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              data-testid="refresh-button"
              onClick={fetchData}
              variant="outline"
              className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              data-testid="export-button"
              onClick={handleExport}
              className="bg-[#E07A5F] hover:bg-[#C66A50] text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <Card data-testid="total-leads-card" className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#64748B]">
                Total Leads
              </CardTitle>
              <Users className="w-5 h-5 text-[#1E3A5F]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#1E3A5F]">
                {stats.total_leads}
              </div>
              <p className="text-xs text-[#84A98C] mt-1">
                +{stats.today_leads} today
              </p>
            </CardContent>
          </Card>
          
          <Card data-testid="today-leads-card" className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#64748B]">
                Today's Signups
              </CardTitle>
              <TrendingUp className="w-5 h-5 text-[#84A98C]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#84A98C]">
                {stats.today_leads}
              </div>
              <p className="text-xs text-[#64748B] mt-1">
                New leads today
              </p>
            </CardContent>
          </Card>
          
          <Card data-testid="download-count-card" className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-[#64748B]">
                Total Downloads
              </CardTitle>
              <Download className="w-5 h-5 text-[#E07A5F]" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#E07A5F]">
                {247 + stats.total_leads}
              </div>
              <p className="text-xs text-[#64748B] mt-1">
                Checklists downloaded
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* Chart Section */}
        {chartData.length > 0 && (
          <Card data-testid="concern-chart-card" className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-[#1E3A5F]">Lead Concerns Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis 
                      dataKey="name" 
                      stroke="#64748B"
                      fontSize={12}
                    />
                    <YAxis stroke="#64748B" fontSize={12} />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1E3A5F',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#1E3A5F" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Leads Table */}
        <Card data-testid="leads-table-card" className="border-0 shadow-lg">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="text-[#1E3A5F]">All Leads</CardTitle>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  data-testid="search-input"
                  type="text"
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-[#E2E8F0] focus:border-[#1E3A5F] focus:ring-[#1E3A5F]"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:text-[#1E3A5F]"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Name
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:text-[#1E3A5F]"
                      onClick={() => handleSort("email")}
                    >
                      <div className="flex items-center gap-1">
                        Email
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                    <TableHead>Concern</TableHead>
                    <TableHead>SMS</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:text-[#1E3A5F]"
                      onClick={() => handleSort("created_at")}
                    >
                      <div className="flex items-center gap-1">
                        Signup Date
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-[#64748B]">
                        {isLoading ? "Loading..." : "No leads found"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredLeads.map((lead) => (
                      <TableRow 
                        key={lead.id}
                        data-testid={`lead-row-${lead.id}`}
                        className="hover:bg-[#F1F5F9]"
                      >
                        <TableCell className="font-medium text-[#1E3A5F]">
                          {lead.name}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-[#64748B]">
                            <Mail className="w-4 h-4" />
                            {lead.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          {lead.concern ? (
                            <Badge 
                              variant="secondary"
                              className="bg-[#1E3A5F]/10 text-[#1E3A5F]"
                            >
                              {concernLabels[lead.concern] || lead.concern}
                            </Badge>
                          ) : (
                            <span className="text-[#94A3B8] text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {lead.sms_opt_in ? (
                            <div className="flex items-center gap-2">
                              <Badge className="bg-[#84A98C] text-white">
                                <MessageSquare className="w-3 h-3 mr-1" />
                                Yes
                              </Badge>
                              {lead.phone && (
                                <span className="text-xs text-[#64748B]">
                                  {lead.phone}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[#94A3B8] text-sm">No</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-[#64748B] text-sm">
                            <Calendar className="w-4 h-4" />
                            {formatDate(lead.created_at)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            <div className="mt-4 text-sm text-[#64748B]">
              Showing {filteredLeads.length} of {leads.length} leads
            </div>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="text-center text-sm text-[#94A3B8] pb-4">
          <a href="/" className="hover:text-[#1E3A5F] transition-colors">
            ← Back to Landing Page
          </a>
        </div>
      </div>
    </div>
  );
}
